import React, { useState, useEffect } from 'react';
import MediaGallery from './MediaGallery';

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    short_title: '',
    long_title: '',
    product_sku: '',
    price: '',
    in_stock: true,
    description: '',
    technical_details: '',
    datasheet_links: '',
    media: [] // Unified media array for images and videos
  });
  const [uploading, setUploading] = useState(false);
  const [videoLinksInput, setVideoLinksInput] = useState('');
  const [videoLinksPreview, setVideoLinksPreview] = useState([]);
  const [datasheetLinks, setDatasheetLinks] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch categories and products in parallel
      const [categoriesResponse, productsResponse] = await Promise.all([
        fetch('/api/admin/categories', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/admin/products', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      }

      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        setProducts(productsData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_id || !formData.short_title || !formData.product_sku || !formData.price) {
      alert('Category, short title, SKU, and price are required');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingProduct 
        ? `/api/admin/products/${editingProduct.id}`
        : '/api/admin/products';
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      // Separate media back into images and videos for backend compatibility
      const { media, ...otherFormData } = formData;
      const images = media.filter(item => item.media_type === 'image');
      const videos = media.filter(item => item.media_type === 'video');
      
      const requestBody = {
        ...otherFormData,
        images,
        videos,
        video_links: formData.video_links,
        datasheet_links: formData.datasheet_links,
        price: parseFloat(formData.price)
      };
      
      console.log('🔄 Sending request body:', requestBody);
      console.log('🖼️ Images being sent:', images);
      console.log('🎥 Videos being sent:', videos);
      console.log('📄 Datasheet links being sent:', formData.datasheet_links);
      console.log('📄 Type of datasheet_links:', typeof formData.datasheet_links);
      console.log('📄 Raw datasheet_links value:', JSON.stringify(formData.datasheet_links));
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        await fetchData();
        handleCloseModal();
        resetForm();
      } else {
        const error = await response.json();
        console.error('❌ Backend error:', error);
        console.error('❌ Error details:', error.details);
        console.error('❌ Error stack:', error.stack);
        alert(`Failed to save product: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to save product:', error);
      alert('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    console.log('Editing product:', product);
    console.log('Product images:', product.images);
    console.log('Product videos:', product.videos);
    
    // Prepare unified media array
    const unifiedMedia = [];
    
    // Add images with media_type
    if (product.images && product.images.length > 0) {
      product.images.forEach(image => {
        unifiedMedia.push({
          ...image,
          media_type: 'image',
          url: image.image_url || image.url
        });
      });
    }
    
    // Add videos with media_type
    if (product.videos && product.videos.length > 0) {
      product.videos.forEach(video => {
        unifiedMedia.push({
          ...video,
          media_type: 'video',
          url: video.video_url || video.url
        });
      });
    }
    
    // Sort by display_order
    unifiedMedia.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    
    setEditingProduct(product);
    setFormData({
      category_id: product.category_id.toString(),
      short_title: product.short_title,
      long_title: product.long_title || '',
      product_sku: product.product_sku,
      price: product.price.toString(),
      in_stock: product.in_stock,
      description: product.description || '',
      technical_details: product.technical_details || '',
      datasheet_links: product.datasheet_links || '',
      media: unifiedMedia,
      video_links: product.video_links || ''
    });
    
    // Populate video links input for editing
    if (product.video_links) {
      try {
        const videoLinks = JSON.parse(product.video_links);
        const videoUrls = videoLinks.map(video => video.url).join('\n');
        setVideoLinksInput(videoUrls);
        setVideoLinksPreview(videoLinks);
      } catch (error) {
        console.error('Error parsing video links:', error);
        setVideoLinksInput('');
        setVideoLinksPreview([]);
      }
    } else {
      setVideoLinksInput('');
      setVideoLinksPreview([]);
    }
    
    // Populate datasheet links for editing
    if (product.datasheet_links) {
      try {
        const datasheetLinks = JSON.parse(product.datasheet_links);
        setDatasheetLinks(datasheetLinks);
      } catch (error) {
        console.error('Error parsing datasheet links:', error);
        setDatasheetLinks([]);
      }
    } else {
      setDatasheetLinks([]);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchData();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      alert('Failed to delete product');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      short_title: '',
      long_title: '',
      product_sku: '',
      price: '',
      in_stock: true,
      description: '',
      technical_details: '',
      datasheet_links: '',
      media: [],
      video_links: ''
    });
    setVideoLinksInput('');
    setVideoLinksPreview([]);
    setDatasheetLinks([]);
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    resetForm();
    setShowModal(true);
  };



  // Helper function to normalize image URLs
  const normalizeImageUrl = (image) => {
    const url = image.image_url || image.url;
    if (!url) return null;
    
    // If it's already a relative URL (our new format), use it as is
    if (url.startsWith('/api/upload/')) {
      return url;
    }
    
    // If it's the old SFTPGo public URL, convert it to our new format
    if (url.includes('sftpgo.caprover.distracker.com.mx/public/webstoreuser/images/')) {
      const filename = url.split('/').pop(); // Get the filename from the end
      return `/api/upload/image/${filename}`;
    }
    
    // If it's the old SFTPGo public URL for videos, convert it
    if (url.includes('sftpgo.caprover.distracker.com.mx/public/webstoreuser/videos/')) {
      const filename = url.split('/').pop();
      return `/api/upload/video/${filename}`;
    }
    
    // If it's the old SFTPGo public URL for datasheets, convert it
    if (url.includes('sftpgo.caprover.distracker.com.mx/public/webstoreuser/datasheets/')) {
      const filename = url.split('/').pop();
      return `/api/upload/datasheet/${filename}`;
    }
    
    // Return as is if we don't recognize the format
    return url;
  };







  const removeDatasheet = async () => {
    if (!formData.datasheet_url) return;

    try {
      // Extract filename from the URL
      let filename;
      const url = formData.datasheet_url;
      
      if (url.includes('/api/upload/datasheet/')) {
        // New format: /api/upload/datasheet/filename
        filename = url.split('/').pop();
      } else if (url.includes('sftpgo.caprover.distracker.com.mx/public/webstoreuser/datasheets/')) {
        // Old format: extract filename from SFTPGo URL
        filename = url.split('/').pop();
      } else {
        console.warn('⚠️ Could not extract filename from URL:', url);
        // Still remove from UI even if we can't delete from server
        setFormData(prev => ({ ...prev, datasheet_url: '' }));
        return;
      }

      // Delete file from SFTPGo
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/upload/files/datasheets/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('✅ Datasheet file deleted from SFTPGo:', filename);
      } else {
        console.warn('⚠️ Failed to delete datasheet file from SFTPGo:', filename);
        const error = await response.json();
        console.warn('⚠️ Error details:', error);
      }

      // Remove from UI regardless of server deletion result
      setFormData(prev => ({ ...prev, datasheet_url: '' }));
    } catch (error) {
      console.error('❌ Error removing datasheet:', error);
      // Still remove from UI even if server deletion fails
      setFormData(prev => ({ ...prev, datasheet_url: '' }));
    }
  };

  // Handle video links input and convert to JSON format
  const handleVideoLinksChange = (e) => {
    const input = e.target.value;
    setVideoLinksInput(input);
    
    // Parse YouTube links from input
    const lines = input.split('\n').filter(line => line.trim());
    const videoLinks = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        // Extract video ID from various YouTube URL formats
        let videoId = null;
        
        // Standard YouTube URLs
        if (trimmedLine.includes('youtube.com/watch?v=')) {
          videoId = trimmedLine.split('v=')[1]?.split('&')[0];
        }
        // Short YouTube URLs
        else if (trimmedLine.includes('youtu.be/')) {
          videoId = trimmedLine.split('youtu.be/')[1]?.split('?')[0];
        }
        // YouTube embed URLs
        else if (trimmedLine.includes('youtube.com/embed/')) {
          videoId = trimmedLine.split('embed/')[1]?.split('?')[0];
        }
        
        if (videoId) {
          videoLinks.push({
            id: videoId,
            url: trimmedLine,
            title: `YouTube Video ${videoId}` // We'll fetch the actual title later if needed
          });
        }
      }
    });
    
    setVideoLinksPreview(videoLinks);
    
    // Update formData with JSON string
    setFormData(prev => ({
      ...prev,
      video_links: JSON.stringify(videoLinks)
    }));
  };

  // Handle adding new datasheet
  const addDatasheetFile = async () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Check file size (50MB limit for datasheets)
      if (file.size > 50 * 1024 * 1024) {
        alert('Datasheet file size must be less than 50MB');
        return;
      }
      
      setUploading(true);
      
      try {
        const token = localStorage.getItem('adminToken');
        const formData = new FormData();
        formData.append('datasheet', file);
        
        const response = await fetch('/api/upload/datasheet', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          
          // Prompt for title
          const title = prompt('Enter a title for this datasheet:', file.name.replace(/\.[^/.]+$/, ""));
          if (title) {
            const newDatasheet = {
              id: `datasheet_${Date.now()}`,
              url: result.file.url,
              title: title,
              type: 'datasheet',
              filename: result.file.filename
            };
            
            const updatedDatasheets = [...datasheetLinks, newDatasheet];
            setDatasheetLinks(updatedDatasheets);
            
            // Update formData with JSON string
            setFormData(prev => ({
              ...prev,
              datasheet_links: JSON.stringify(updatedDatasheets)
            }));
          }
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to upload datasheet');
        }
      } catch (error) {
        console.error('Error uploading datasheet:', error);
        alert('Failed to upload datasheet');
      } finally {
        setUploading(false);
      }
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  // Handle removing datasheet
  const removeDatasheetFile = (datasheetId) => {
    const updatedDatasheets = datasheetLinks.filter(ds => ds.id !== datasheetId);
    setDatasheetLinks(updatedDatasheets);
    
    // Update formData with JSON string
    setFormData(prev => ({
      ...prev,
      datasheet_links: JSON.stringify(updatedDatasheets)
    }));
  };

    const addDatasheet = async () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Check file size (50MB limit for datasheets)
      if (file.size > 50 * 1024 * 1024) {
        alert('Datasheet file size must be less than 50MB');
        return;
      }
      
      setUploading(true);
      
      try {
        const token = localStorage.getItem('adminToken');
        const formData = new FormData();
        formData.append('datasheet', file);
        
        const response = await fetch('/api/upload/datasheet', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setFormData(prev => ({
            ...prev,
            datasheet_url: result.file.url
          }));
          
          // Show success message
          setTimeout(() => {
            setUploading(false);
          }, 500);
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to upload datasheet');
          setUploading(false);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload datasheet');
        setUploading(false);
      }
      
      // Clean up
      document.body.removeChild(fileInput);
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  // New Media Gallery Management Functions
  const handleMediaChange = (newMedia) => {
    setFormData(prev => ({
      ...prev,
      media: newMedia
    }));
  };

  const handleMediaReorder = (newOrder) => {
    setFormData(prev => ({
      ...prev,
      media: newOrder
    }));
  };

  const handleMediaDelete = async (mediaId, mediaType) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/media/${mediaType}/${mediaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setFormData(prev => ({
          ...prev,
          media: prev.media.filter(item => item.id !== mediaId)
        }));
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete media');
      }
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete media');
    }
  };

  const handleMediaUpload = async (uploadedMedia) => {
    console.log('🔄 handleMediaUpload called with:', uploadedMedia);
    
    // Add all uploaded media to the gallery
    if (uploadedMedia && uploadedMedia.length > 0) {
      // Update form data first
      setFormData(prev => {
        const updatedFormData = {
          ...prev,
          media: [...(prev.media || []), ...uploadedMedia]
        };
        
        // Auto-save the product to database after media upload (only when editing existing product)
        if (editingProduct && editingProduct.id) {
          console.log('🔄 Starting auto-save after media upload...');
          console.log('📝 Editing product:', editingProduct);
          console.log('📝 Updated form data media:', updatedFormData.media);
          
          // Use setTimeout to ensure state update completes before auto-save
          setTimeout(() => {
            autoSaveProduct(updatedFormData);
          }, 0);
        } else {
          console.log('ℹ️ Skipping auto-save - creating new product');
          console.log('📝 Editing product state:', editingProduct);
        }
        
        return updatedFormData;
      });
    }
  };

  // Auto-save function to save product data after media uploads
  const autoSaveProduct = async (updatedFormData = null) => {
    console.log('🔍 Auto-save function called');
    console.log('🔍 Editing product:', editingProduct);
    
    // Use the passed form data if available, otherwise use current state
    const formDataToUse = updatedFormData || formData;
    console.log('🔍 Form data to use:', formDataToUse);
    
    if (!editingProduct || !editingProduct.id) {
      console.log('❌ No product being edited, skipping auto-save');
      return;
    }
    
    // Prevent auto-save if user is actively typing or making changes
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      console.log('⏳ User is typing, delaying auto-save...');
      setTimeout(() => autoSaveProduct(updatedFormData), 1000); // Delay by 1 second
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      console.log('🔑 Token:', token ? 'Present' : 'Missing');
      
      // Separate media back into images and videos for backend compatibility
      const { media, ...otherFormData } = formDataToUse;
      const images = media.filter(item => item.media_type === 'image');
      const videos = media.filter(item => item.media_type === 'video');
      
      const requestBody = {
        ...otherFormData,
        images,
        videos,
        price: parseFloat(formDataToUse.price)
      };
  
      console.log('📤 Auto-saving product with media:', { 
        images: images.length, 
        videos: videos.length,
        requestBody: requestBody
      });
      
      const response = await fetch(`/api/admin/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });      
      
      console.log('📥 Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText
      });
  
      if (response.ok) {
        console.log('✅ Product auto-saved successfully');
        // Update the editingProduct with new data to keep it in sync
        const updatedProduct = await response.json();
        console.log('📝 Updated product:', updatedProduct);
        setEditingProduct(updatedProduct);
        // Also refresh the products list to show the new media
        await fetchData();
        console.log('🔄 Products list refreshed');
      } else {
        const error = await response.json();
        console.error('❌ Auto-save failed:', error.error || 'Unknown error');
        console.error('❌ Full error response:', error);
        // Don't show alert for auto-save failures to avoid interrupting user
      }
    } catch (error) {
      console.error('❌ Auto-save error:', error);
      console.error('❌ Error stack:', error.stack);
      // Don't show alert for auto-save failures to avoid interrupting user
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="product-manager">
      <div className="manager-header">
        <h2>Product Management</h2>
        <button onClick={handleAddNew} className="btn btn-primary">
          Add New Product
        </button>
      </div>

      <div className="products-list">
        {products.length === 0 ? (
          <div className="empty-state">
            <p>No products found. Create your first product to get started.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    {(product.images && product.images.length > 0) || (product.videos && product.videos.length > 0) ? (
                      (() => {
                        // Try to find first image, then first video
                        const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
                        const firstVideo = product.videos && product.videos.length > 0 ? product.videos[0] : null;
                        
                        if (firstImage) {
                          return (
                            <img 
                              src={normalizeImageUrl(firstImage)} 
                              alt={product.short_title}
                              className="product-thumbnail"
                              onError={(e) => {
                                console.error('Product thumbnail failed to load:', normalizeImageUrl(firstImage));
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => console.log('Product thumbnail loaded successfully:', normalizeImageUrl(firstImage))}
                            />
                          );
                        } else if (firstVideo) {
                          return (
                            <video 
                              src={normalizeImageUrl(firstVideo)} 
                              className="product-thumbnail"
                              muted
                              onError={(e) => {
                                console.error('Product video thumbnail failed to load:', normalizeImageUrl(firstVideo));
                                e.target.style.display = 'none';
                              }}
                            />
                          );
                        }
                        return <div className="no-image">No Media</div>;
                      })()
                    ) : (
                      <div className="no-image">No Media</div>
                    )}
                  </td>
                  <td>
                    <div>
                      <strong>{product.short_title}</strong>
                      {product.long_title && (
                        <div className="long-title">{product.long_title}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <code>{product.product_sku}</code>
                  </td>
                  <td>{product.category_name}</td>
                  <td>${product.price}</td>
                  <td>
                    <span className={`status-badge ${product.in_stock ? 'status-active' : 'status-inactive'}`}>
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        onClick={() => handleEdit(product)}
                        className="btn btn-secondary btn-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button onClick={handleCloseModal} className="modal-close">×</button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="productSku">Product SKU *</label>
                  <input
                    type="text"
                    id="productSku"
                    value={formData.product_sku}
                    onChange={(e) => setFormData({ ...formData, product_sku: e.target.value })}
                    placeholder="Enter product SKU"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="shortTitle">Short Title *</label>
                <input
                  type="text"
                  id="shortTitle"
                  value={formData.short_title}
                  onChange={(e) => setFormData({ ...formData, short_title: e.target.value })}
                  placeholder="Enter short title"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="longTitle">Long Title</label>
                <textarea
                  id="longTitle"
                  value={formData.long_title}
                  onChange={(e) => setFormData({ ...formData, long_title: e.target.value })}
                  placeholder="Enter detailed title (optional)"
                  rows="2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Price *</label>
                  <input
                    type="number"
                    id="price"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="inStock">Stock Status</label>
                  <select
                    id="inStock"
                    value={formData.in_stock.toString()}
                    onChange={(e) => setFormData({ ...formData, in_stock: e.target.value === 'true' })}
                  >
                    <option value="true">In Stock</option>
                    <option value="false">Out of Stock</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter product description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="technicalDetails">Technical Details</label>
                <textarea
                  id="technicalDetails"
                  value={formData.technical_details}
                  onChange={(e) => setFormData({ ...formData, technical_details: e.target.value })}
                  placeholder="Enter technical specifications"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="datasheetUrl">Datasheet URL</label>
                <div className="datasheet-url-input">
                  <input
                    type="url"
                    id="datasheetUrl"
                    value={formData.datasheet_url}
                    onChange={(e) => setFormData({ ...formData, datasheet_url: e.target.value })}
                    placeholder="Upload a file or enter URL manually"
                    readOnly={!!formData.datasheet_url}
                  />
                  {formData.datasheet_url && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, datasheet_url: '' }))}
                      className="btn btn-sm btn-secondary"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="file-info">
                  Or upload a file using the button below
                </div>
              </div>

              <div className="form-group">
                <label>Product Media Gallery</label>
                <MediaGallery
                  productId={editingProduct?.id || 0}
                  media={formData.media || []}
                  onMediaChange={handleMediaChange}
                  onReorder={handleMediaReorder}
                  onDelete={handleMediaDelete}
                  onUpload={handleMediaUpload}
                  isEditing={true}
                />
              </div>

              <div className="form-group">
                <label>Product Datasheets</label>
                <div className="datasheet-management">
                  <button
                    type="button"
                    onClick={addDatasheetFile}
                    className="btn btn-secondary"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Add Datasheet'}
                  </button>
                  
                  <div className="file-info">
                    Supported formats: PDF, DOC, DOCX, XLS, XLSX • Max size: 50MB
                  </div>
                  
                  {/* Current Datasheets */}
                  {datasheetLinks.length > 0 && (
                    <div className="datasheet-list">
                      <h4>Current Datasheets ({datasheetLinks.length}):</h4>
                      {datasheetLinks.map((datasheet, index) => (
                        <div key={datasheet.id} className="datasheet-item">
                          <div className="datasheet-info">
                            <strong>{datasheet.title}</strong>
                            <span className="datasheet-filename">({datasheet.filename})</span>
                          </div>
                          <div className="datasheet-actions">
                            <a 
                              href={datasheet.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline"
                            >
                              View
                            </a>
                            <button
                              type="button"
                              onClick={() => removeDatasheetFile(datasheet.id)}
                              className="btn btn-sm btn-danger"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Video Links Section */}
                  <div className="form-group">
                    <label htmlFor="video-links">Video Links (YouTube)</label>
                    <div className="video-links-container">
                      <textarea
                        id="video-links"
                        placeholder="Paste YouTube links here, one per line:&#10;https://www.youtube.com/watch?v=abc123&#10;https://youtu.be/def456&#10;https://www.youtube.com/watch?v=ghi789"
                        value={videoLinksInput}
                        onChange={handleVideoLinksChange}
                        rows={4}
                        className="video-links-textarea"
                      />
                      <div className="video-links-info">
                        Paste YouTube links (one per line). They will be automatically formatted and saved.
                      </div>
                      {videoLinksPreview.length > 0 && (
                        <div className="video-links-preview">
                          <strong>Preview ({videoLinksPreview.length} video{videoLinksPreview.length !== 1 ? 's' : ''}):</strong>
                          <ul>
                            {videoLinksPreview.map((video, index) => (
                              <li key={index}>
                                <a href={video.url} target="_blank" rel="noopener noreferrer">
                                  {video.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager; 